import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { firebaseApp } from '../../firebase-config';
import Sign from '../../components/Sign/Sign';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/userContext';
import { authService } from '../../services/auth.service';
import { ReactComponent as EyeOpen } from '../../assets/icons/eye-open.svg';
import { ReactComponent as EyeClosed } from '../../assets/icons/eye-closed.svg';

const STORAGE_RESELLER_KEY = 'truhu_activation_reseller';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const returnUrl = query.get('returnUrl');

  const {
    state: { isLoggedIn }
  } = useUser();

  const [resellerLogo, setResellerLogo] = useState<string | undefined>();
  const [resellerName, setResellerName] = useState<string | undefined>();

  useEffect(() => {
    if (returnUrl && returnUrl.includes('/activate')) {
      try {
        const storedReseller = sessionStorage.getItem(STORAGE_RESELLER_KEY);
        if (storedReseller) {
          const parsed = JSON.parse(storedReseller);
          setResellerLogo(parsed.logoUrl);
          setResellerName(parsed.name);
        }
      } catch (e) {
        // Silently fail if sessionStorage data is invalid
      }
    }
  }, [returnUrl]);

  useEffect(() => {
    if (isLoggedIn) {
      if (returnUrl) {
        navigate(returnUrl);
      } else {
        navigate('/download');
      }
    }
  }, [isLoggedIn]);

  const authentication = getAuth(firebaseApp);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    onSubmit: async (values) => {
      setError('');

      if (values.email && values.password) {
        try {
          await signInWithEmailAndPassword(authentication, values.email, values.password);

          if (authentication.currentUser === null) {
            return;
          }

          if (authentication.currentUser.displayName === null) {
            return;
          }

          const response = await authService.userExistsCheck(authentication.currentUser.uid);
          if (!response.responseObject) {
            const splitOnSpace = authentication.currentUser.displayName.split(' ');
            const firstName = splitOnSpace[0];
            const lastName = splitOnSpace[splitOnSpace.length - 1];

            const userResponse = await authService.createUserInfo(
              authentication.currentUser.uid,
              values.email,
              firstName,
              lastName
            );

            if (!userResponse.isSuccess) {
              setError(userResponse.errors[0]);
            }
          }
        } catch (error: any) {
          setError(error.code);
        }
      }
    }
  });

  const signupLink = returnUrl ? `/signup?returnUrl=${encodeURIComponent(returnUrl)}` : '/signup';

  return (
    <Sign resellerLogo={resellerLogo} resellerName={resellerName}>
      <form onSubmit={formik.handleSubmit} className="register">
        <h2>Sign In</h2>
        <p>Link your desktop / mobile App to your TruHu account.</p>
        <p>
          Don&apos;t have an account yet?{' '}
          <span>
            <Link to={signupLink} style={{ color: '#59bdef', textDecoration: 'underline' }}>
              Sign up
            </Link>
          </span>{' '}
          here.
        </p>
        <label>E-mail</label>
        {error === 'auth/user-not-found' ||
        error === 'auth/invalid-credential' ||
        error === 'auth/invalid-login-credentials' ||
        error === 'auth/wrong-password' ? (
          <div>
            <input
              id="email"
              name="email"
              className="error-login"
              type="email"
              onChange={formik.handleChange}
              value={formik.values.email}
              placeholder="name@email.com"
            />
            <span className="span__error-login">Invalid email or password</span>
          </div>
        ) : (
          <input
            id="email"
            name="email"
            type="email"
            onChange={formik.handleChange}
            required
            value={formik.values.email}
            placeholder="name@email.com"
          />
        )}
        <label>Password</label>
        {error === 'auth/wrong-password' ||
        error === 'auth/invalid-credential' ||
        error === 'auth/invalid-login-credentials' ||
        error === 'auth/user-not-found' ? (
          <div>
            <div className="input-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="error-login"
                id="password"
                onChange={formik.handleChange}
                value={formik.values.password}
                placeholder="6+ Characters, 1 Capital letter"
              />
              <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeClosed /> : <EyeOpen />}
              </span>
            </div>
            <span className="span__error-login">Invalid email or password</span>
          </div>
        ) : error === 'auth/too-many-requests' ? (
          <div>
            <div className="input-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="error-login"
                id="password"
                onChange={formik.handleChange}
                value={formik.values.password}
                placeholder="6+ Characters, 1 Capital letter"
              />
              <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeClosed /> : <EyeOpen />}
              </span>
            </div>
            <span className="span__error-login">
              Access to this account has been temporarily disabled due to many failed login
              attempts. You can immediately restore it by resetting your password or you can try
              again later.
            </span>
          </div>
        ) : (
          <div className="input-password-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              required
              onChange={formik.handleChange}
              value={formik.values.password}
              placeholder="6+ Characters, 1 Capital letter"
            />
            <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeClosed /> : <EyeOpen />}
            </span>
          </div>
        )}
        <p onClick={() => navigate('/reset-password')} className="cursor-pointer">
          <span>Forgot password?</span>
        </p>
        <button type="submit">Sign in</button>
      </form>
    </Sign>
  );
};

export default LoginForm;
